import { Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionPricing from '../../_Sections/SectionPricing';
import SectionSizes from '../../_Sections/SectionSizes';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionPairings from '../../_Sections/SectionPairings';
import ProductSEO from '../../_Sections/ProductSEO';
import fabricIcon from '@resources/img/pages/products/matte-crepe-ruffle-scrunchie/icon-1.svg?url';
import sizeIcon from '@resources/img/pages/products/ruffle-scrunchie/icon-2.svg?url';
import finishingIcon from '@resources/img/pages/products/ruffle-scrunchie/icon-3.svg?url';
import inkIcon from '@resources/img/pages/products/ruffle-scrunchie/icon-4.svg?url';
import canadaIcon from '@resources/img/pages/products/ruffle-scrunchie/icon-5.svg?url';
import measurementsUrl from '@resources/img/pages/products/ruffle-scrunchie/measurements.svg?url';

const PRODUCT_ID = 144;
const VIEW_PRODUCT_ID = 174;

const bannerImages = {
  en_CA: new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/banner-1-en_CA.png', import.meta.url).href,
  fr_CA: new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/banner-1-fr_CA.png', import.meta.url).href,
};

export default function MatteCrepeRuffleScrunchiePage() {
  const [t] = useTranslation('page/products/matte-crepe-ruffle-scrunchies');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));

  if (!productData) return null;

  return <Box className="centered-page product-page-v4">
    <ProductSEO viewProductId={VIEW_PRODUCT_ID} title={t('seo-title')} description={t('meta-description')} />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#DFF2F5"
      bgStyle={{ backgroundPosition: 'bottom right', backgroundRepeat: 'no-repeat', backgroundSize: 'min(55%, 750px)' }}
      fullPageBg
      desktopImageStyle={{ objectFit: 'contain' }}
      mobileImageStyle={{ display: 'block', width: 'auto', maxWidth: '100%', maxHeight: 'min(60svh, 520px)', marginLeft: 'auto' }}
      tag={t('All-over print')}
      title={t('product-name')}
      text={t('intro')}
      createUrl={productData.get('url_slug') + '/fabric-matte-crepe'}
      img={locale === 'fr_CA' ? bannerImages.fr_CA : bannerImages.en_CA}
      alt={t('image-alt')}
      details={[
        { img: fabricIcon, alt: t('Fabric icon'), text: t('fabric-feature') },
        { img: sizeIcon, alt: t('Size icon'), text: t('Striking, oversized design') },
        { img: finishingIcon, alt: t('Finishing icon'), text: t('Rolled-edge finishing complements your design') },
        { img: inkIcon, alt: t('Ink icon'), text: t('ink-feature') },
        { img: canadaIcon, alt: t('Canada icon'), text: t('Digitally printed on demand in Montreal, Canada') },
      ]}
    />
    <SectionGallery images={[
      new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/gallery_0.jpg', import.meta.url).href,
      new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/gallery_1.jpg', import.meta.url).href,
      new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/gallery_2.jpg', import.meta.url).href,
      new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/gallery_3.jpg', import.meta.url).href,
      new URL('@resources/img/pages/products/matte-crepe-ruffle-scrunchie/gallery_4.jpg', import.meta.url).href,
    ]} />
    <SectionDropship
      title={<Trans t={t} i18nKey="dropship-title" components={{ yellow: <span className="ty" />, pink: <span className="tp" /> }} />}
      text={t('dropship-text')}
    />
    <SectionPricing productId={PRODUCT_ID} options={['matte-crepe']} />
    <SectionSizes productId={PRODUCT_ID} title={t('sizing-title')} text={<Trans t={t} i18nKey="sizing-text" components={{
      width: <SizeDisplay sizes={[2.5]} sourceUnit="in" displayType="physical" />,
      opening: <SizeDisplay sizes={[2]} sourceUnit="in" displayType="physical" />,
    }} />}>
      <RuffleScrunchieMeasurementDiagram alt={t('measurements-alt')} />
    </SectionSizes>
    <SectionBranding title={t('branding-title')} text={t('branding-text')} />
    <SectionPairings text={t('pairings-text')} productSlugs={['polychiffon-scarves', 'dad-cap-with-printed-patch', 'stainless-steel-water-bottle']} />
  </Box>;
}

function RuffleScrunchieMeasurementDiagram({ alt }: { alt: string }) {
  return <Box
    component="svg"
    viewBox="0 0 626.489 749.175"
    role="img"
    aria-label={alt}
    sx={{
      display: 'block',
      width: '100%',
      maxWidth: { xs: 260, sm: 320 },
      height: 'auto',
      mx: 'auto',
      mt: { xs: 3, sm: 4 },
      '& text': {
        fill: 'currentColor',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 32,
        fontWeight: 500,
      },
    }}
  >
    <image aria-hidden href={measurementsUrl} width="626.489" height="749.175" />
    <text x="313.245" y="372" textAnchor="middle">
      <SizeDisplay sizes={[2]} sourceUnit="in" displayType="physical" />
    </text>
    <text x="526" y="741" textAnchor="middle">
      <SizeDisplay sizes={[2.5]} sourceUnit="in" displayType="physical" />
    </text>
  </Box>;
}
